import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, MapPin, Edit, Archive } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const emptyFormData = {
  client_id: '',
  address_line1: '',
  address_line2: '',
  city: '',
  postcode: '',
  access_notes: {
    alarm_code: '',
    key_location: '',
    pets: '',
    parking: '',
    other: ''
  }
};

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationToArchive, setLocationToArchive] = useState(null);
  const [filter, setFilter] = useState('active');
  
  // Separate state for create and edit forms
  const [createFormData, setCreateFormData] = useState(emptyFormData);
  const [editFormData, setEditFormData] = useState(emptyFormData);

  const fetchLocations = useCallback(async (currentFilter) => {
    try {
      let url = `${API_URL}/api/locations`;
      if (currentFilter === 'active') {
        url += '?is_active=true';
      } else if (currentFilter === 'archived') {
        url += '?is_active=false';
      }
      const response = await axios.get(url);
      setLocations(response.data);
    } catch (error) {
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients?is_active=true`);
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    }
  }, []);

  useEffect(() => {
    fetchLocations(filter);
    fetchClients();
  }, [filter, fetchLocations, fetchClients]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/locations`, createFormData);
      toast.success('Location created successfully');
      setDialogOpen(false);
      setCreateFormData(emptyFormData);
      fetchLocations(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create location');
    }
  };

  const handleEdit = (location) => {
    setSelectedLocation(location);
    setEditFormData({
      client_id: location.client_id,
      address_line1: location.address_line1 || '',
      address_line2: location.address_line2 || '',
      city: location.city || '',
      postcode: location.postcode || '',
      access_notes: {
        alarm_code: location.access_notes?.alarm_code || '',
        key_location: location.access_notes?.key_location || '',
        pets: location.access_notes?.pets || '',
        parking: location.access_notes?.parking || '',
        other: location.access_notes?.other || ''
      }
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API_URL}/api/locations/${selectedLocation.id}`, {
        address_line1: editFormData.address_line1,
        address_line2: editFormData.address_line2,
        city: editFormData.city,
        postcode: editFormData.postcode,
        access_notes: editFormData.access_notes
      });
      toast.success('Location updated successfully');
      setEditDialogOpen(false);
      setSelectedLocation(null);
      setEditFormData(emptyFormData);
      fetchLocations(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update location');
    }
  };

  const openArchiveDialog = (location) => {
    setLocationToArchive(location);
    setArchiveDialogOpen(true);
  };

  const handleArchive = async (locationId) => {
    try {
      await axios.patch(`${API_URL}/api/locations/${locationId}/archive`);
      toast.success('Location archived');
      fetchLocations(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive location');
    } finally {
      setArchiveDialogOpen(false);
      setLocationToArchive(null);
    }
  };

  const handleRestore = async (locationId) => {
    try {
      await axios.patch(`${API_URL}/api/locations/${locationId}/restore`);
      toast.success('Location restored');
      fetchLocations(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore location');
    }
  };

  // Stable handlers for CREATE form using functional updates
  const handleCreateClientChange = useCallback((value) => {
    setCreateFormData(prev => ({ ...prev, client_id: value }));
  }, []);

  const handleCreateAddress1Change = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({ ...prev, address_line1: value }));
  }, []);

  const handleCreateAddress2Change = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({ ...prev, address_line2: value }));
  }, []);

  const handleCreateCityChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({ ...prev, city: value }));
  }, []);

  const handleCreatePostcodeChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({ ...prev, postcode: value }));
  }, []);

  const handleCreateAlarmChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({
      ...prev,
      access_notes: { ...prev.access_notes, alarm_code: value }
    }));
  }, []);

  const handleCreateKeyChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({
      ...prev,
      access_notes: { ...prev.access_notes, key_location: value }
    }));
  }, []);

  const handleCreatePetsChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({
      ...prev,
      access_notes: { ...prev.access_notes, pets: value }
    }));
  }, []);

  const handleCreateParkingChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({
      ...prev,
      access_notes: { ...prev.access_notes, parking: value }
    }));
  }, []);

  const handleCreateOtherChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({
      ...prev,
      access_notes: { ...prev.access_notes, other: value }
    }));
  }, []);

  // Stable handlers for EDIT form using functional updates
  const handleEditAddress1Change = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({ ...prev, address_line1: value }));
  }, []);

  const handleEditAddress2Change = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({ ...prev, address_line2: value }));
  }, []);

  const handleEditCityChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({ ...prev, city: value }));
  }, []);

  const handleEditPostcodeChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({ ...prev, postcode: value }));
  }, []);

  const handleEditAlarmChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({
      ...prev,
      access_notes: { ...prev.access_notes, alarm_code: value }
    }));
  }, []);

  const handleEditKeyChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({
      ...prev,
      access_notes: { ...prev.access_notes, key_location: value }
    }));
  }, []);

  const handleEditPetsChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({
      ...prev,
      access_notes: { ...prev.access_notes, pets: value }
    }));
  }, []);

  const handleEditParkingChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({
      ...prev,
      access_notes: { ...prev.access_notes, parking: value }
    }));
  }, []);

  const handleEditOtherChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({
      ...prev,
      access_notes: { ...prev.access_notes, other: value }
    }));
  }, []);

  // Stable dialog handlers
  const handleCreateDialogChange = useCallback((open) => {
    setDialogOpen(open);
    if (!open) {
      setCreateFormData(emptyFormData);
    }
  }, []);

  const handleEditDialogChange = useCallback((open) => {
    setEditDialogOpen(open);
    if (!open) {
      setSelectedLocation(null);
      setEditFormData(emptyFormData);
    }
  }, []);

  return (
    <Layout>
      <div className="space-y-6" data-testid="locations-page">
        <div className="flex justify-end items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="location-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={dialogOpen} onOpenChange={handleCreateDialogChange}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600" data-testid="add-location-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Location
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Location</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="location-form">
                <div>
                  <Label htmlFor="client_id">Client</Label>
                  <Select value={createFormData.client_id || undefined} onValueChange={handleCreateClientChange}>
                    <SelectTrigger data-testid="location-client-select">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.filter(c => c.id).map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  </div>
                  <div>
                    <Label htmlFor="address_line1">Address Line 1</Label>
                    <Input
                      id="address_line1"
                      value={createFormData.address_line1}
                      onChange={handleCreateAddress1Change}
                      required
                      data-testid="location-address1-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address_line2">Address Line 2</Label>
                    <Input
                      id="address_line2"
                      value={createFormData.address_line2}
                      onChange={handleCreateAddress2Change}
                      data-testid="location-address2-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={createFormData.city}
                        onChange={handleCreateCityChange}
                        required
                        data-testid="location-city-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="postcode">Postcode</Label>
                      <Input
                        id="postcode"
                        value={createFormData.postcode}
                        onChange={handleCreatePostcodeChange}
                        required
                        data-testid="location-postcode-input"
                      />
                    </div>
                  </div>
                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <h4 className="text-sm font-bold text-slate-700 mb-3">Access Notes</h4>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="alarm_code">Alarm Code</Label>
                        <Input
                          id="alarm_code"
                          value={createFormData.access_notes.alarm_code}
                          onChange={handleCreateAlarmChange}
                          data-testid="location-alarm-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="key_location">Key Location</Label>
                        <Input
                          id="key_location"
                          value={createFormData.access_notes.key_location}
                          onChange={handleCreateKeyChange}
                          data-testid="location-key-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="pets">Pets</Label>
                        <Input
                          id="pets"
                          value={createFormData.access_notes.pets}
                          onChange={handleCreatePetsChange}
                          data-testid="location-pets-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="parking">Parking</Label>
                        <Input
                          id="parking"
                          value={createFormData.access_notes.parking}
                          onChange={handleCreateParkingChange}
                          data-testid="location-parking-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="other">Other Notes</Label>
                        <Textarea
                          id="other"
                          value={createFormData.access_notes.other}
                          onChange={handleCreateOtherChange}
                          rows={2}
                          data-testid="location-other-input"
                        />
                      </div>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="submit-location-button">
                    Create Location
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
        </div>

        {loading ? (
          <div>Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {locations.map((location) => (
              <div
                key={location.id}
                className={`bg-white border p-6 hover:border-slate-300 transition-colors ${
                  location.is_active ? 'border-slate-200' : 'border-slate-300 bg-slate-50'
                }`}
                data-testid={`location-card-${location.id}`}
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-100 border border-orange-200 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{location.client_name}</h3>
                      {!location.is_active && (
                        <span className="px-2 py-0.5 text-xs font-bold bg-slate-200 text-slate-700 border border-slate-300">
                          ARCHIVED
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {location.address_line1}
                      {location.address_line2 && `, ${location.address_line2}`}
                    </p>
                    <p className="text-sm text-slate-600">
                      {location.city}, {location.postcode}
                    </p>
                  </div>
                </div>
                {location.access_notes && (
                  <div className="mt-4 pt-4 border-t border-slate-200 space-y-2 text-xs">
                    {location.access_notes.alarm_code && (
                      <p><span className="font-semibold text-slate-700">Alarm:</span> {location.access_notes.alarm_code}</p>
                    )}
                    {location.access_notes.key_location && (
                      <p><span className="font-semibold text-slate-700">Keys:</span> {location.access_notes.key_location}</p>
                    )}
                    {location.access_notes.pets && (
                      <p><span className="font-semibold text-slate-700">Pets:</span> {location.access_notes.pets}</p>
                    )}
                    {location.access_notes.parking && (
                      <p><span className="font-semibold text-slate-700">Parking:</span> {location.access_notes.parking}</p>
                    )}
                  </div>
                )}
                <div className="flex gap-2 pt-3 mt-4 border-t border-slate-200">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(location)}
                    className="flex-1"
                    data-testid={`edit-location-${location.id}`}
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  {location.is_active ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openArchiveDialog(location)}
                      className="border-slate-200 text-slate-600 hover:bg-slate-50"
                      data-testid={`archive-location-${location.id}`}
                    >
                      <Archive className="w-3 h-3 mr-1" />
                      Archive
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestore(location.id)}
                      className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                      data-testid={`restore-location-${location.id}`}
                    >
                      Restore
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Location Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={handleEditDialogChange}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Location</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4" data-testid="edit-location-form">
              <div>
                <Label htmlFor="edit_address_line1">Address Line 1</Label>
                <Input
                  id="edit_address_line1"
                  value={editFormData.address_line1}
                  onChange={handleEditAddress1Change}
                  required
                  data-testid="edit-location-address1-input"
                />
              </div>
              <div>
                <Label htmlFor="edit_address_line2">Address Line 2</Label>
                <Input
                  id="edit_address_line2"
                  value={editFormData.address_line2}
                  onChange={handleEditAddress2Change}
                  data-testid="edit-location-address2-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_city">City</Label>
                  <Input
                    id="edit_city"
                    value={editFormData.city}
                    onChange={handleEditCityChange}
                    required
                    data-testid="edit-location-city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_postcode">Postcode</Label>
                  <Input
                    id="edit_postcode"
                    value={editFormData.postcode}
                    onChange={handleEditPostcodeChange}
                    required
                    data-testid="edit-location-postcode-input"
                  />
                </div>
              </div>
              <div className="border-t border-slate-200 pt-4 mt-4">
                <h4 className="text-sm font-bold text-slate-700 mb-3">Access Notes</h4>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="edit_alarm_code">Alarm Code</Label>
                    <Input
                      id="edit_alarm_code"
                      value={editFormData.access_notes.alarm_code}
                      onChange={handleEditAlarmChange}
                      data-testid="edit-location-alarm-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_key_location">Key Location</Label>
                    <Input
                      id="edit_key_location"
                      value={editFormData.access_notes.key_location}
                      onChange={handleEditKeyChange}
                      data-testid="edit-location-key-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_pets">Pets</Label>
                    <Input
                      id="edit_pets"
                      value={editFormData.access_notes.pets}
                      onChange={handleEditPetsChange}
                      data-testid="edit-location-pets-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_parking">Parking</Label>
                    <Input
                      id="edit_parking"
                      value={editFormData.access_notes.parking}
                      onChange={handleEditParkingChange}
                      data-testid="edit-location-parking-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_other">Other Notes</Label>
                    <Textarea
                      id="edit_other"
                      value={editFormData.access_notes.other}
                      onChange={handleEditOtherChange}
                      rows={2}
                      data-testid="edit-location-other-input"
                    />
                  </div>
                </div>
              </div>
              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="update-location-button">
                Update Location
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Archive Confirmation Dialog */}
        <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Archive Location</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to archive this location at "{locationToArchive?.address_line1}"? No new visits can be created for archived locations.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => handleArchive(locationToArchive?.id)}
                className="bg-orange-500 hover:bg-orange-600"
              >
                Archive
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
